(() => {
  'use strict';

  const normalize = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();

  const isProteinOption = (name) =>
    normalize(name).includes('proteina');

  function readJSON(element, fallback) {
    if (!element) return fallback;

    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error('Kenzu: JSON inválido.', error);
      return fallback;
    }
  }

  function addCount(map, variantId, quantity = 1) {
    if (!variantId || quantity <= 0) return;

    const key = String(variantId);

    map.set(
      key,
      (map.get(key) || 0) + quantity
    );
  }

  function getQuantity(input) {
    const parsed = Number.parseInt(
      input?.value || '1',
      10
    );

    return Math.max(
      1,
      Number.isFinite(parsed) ? parsed : 1
    );
  }

  function setButtonLoading(button, loading) {
    if (!button) return;

    button.disabled = loading;

    button.setAttribute(
      'aria-disabled',
      loading ? 'true' : 'false'
    );

    button.classList.toggle(
      'loading',
      loading
    );

    button
      .querySelector('.loading__spinner')
      ?.classList.toggle(
        'hidden',
        !loading
      );
  }

  function groupSequence(sequence) {
    const counts = new Map();

    sequence.forEach((name) => {
      counts.set(
        name,
        (counts.get(name) || 0) + 1
      );
    });

    return counts;
  }

  class KenzuPromoBuilder {
    constructor(root) {
      this.root = root;

      this.form = document.getElementById(
        root.dataset.productFormId
      );

      this.quantityInput =
        document.querySelector(
          root.dataset.quantitySelector
        );

      this.productId = String(
        root.dataset.productId || ''
      );

      this.productTitle =
        root.dataset.productTitle ||
        'Promoción';

      this.quantity = getQuantity(
        this.quantityInput
      );

      this.proteinCatalog = readJSON(
        root.querySelector(
          '[data-kenzu-protein-catalog]'
        ),
        []
      );

      this.extraData = readJSON(
        root.querySelector(
          '[data-kenzu-extra-data]'
        ),
        {
          freeSauces: 0,
          freeCondiments: 0,
          sauces: [],
          condiments: []
        }
      );

      /*
       * NUEVO:
       * Configuración global para Sin nori y Cevichito.
       */
      this.preparationData = readJSON(
        root.querySelector(
          '[data-kenzu-preparation-data]'
        ),
        {
          withoutNori: {
            variantId: null,
            formattedPrice: ''
          },

          cevichito: {
            variantId: null,
            formattedPrice: '',
            productTitle: 'Cevichito Kenzu'
          }
        }
      );

      this.proteinMap = new Map();

      this.proteinCatalog
        .filter(
          (entry) =>
            entry.active !== false
        )
        .forEach((entry) => {
          this.proteinMap.set(
            normalize(entry.name),
            entry
          );
        });

      this.groups = new Map();
      this.selections = new Map();

      this.sauceSequence = [];
      this.condimentSequence = [];

      this.modal = root.querySelector(
        '[data-kenzu-modal]'
      );

      this.modalBody = root.querySelector(
        '[data-kenzu-modal-body]'
      );

      this.modalTitle = root.querySelector(
        '[data-kenzu-modal-title]'
      );

      this.currentSelection = null;

      this.submitButton =
        this.form?.querySelector(
          'button[type="submit"]'
        );

      this.submitLabel =
        this.submitButton?.querySelector(
          'span'
        );

      this.defaultSubmitText =
        this.submitLabel?.textContent.trim() ||
        'Agregar al carrito';

      this.isSubmitting = false;

      this.handleSubmitCapture =
        this.handleSubmitCapture.bind(this);

      this.initializeGroups();
      this.initializeCarousels();
      this.bindEvents();
      this.update();
    }

    initializeGroups() {
      this.root
        .querySelectorAll(
          '[data-kenzu-group]'
        )
        .forEach((element) => {
          const index = String(
            element.dataset.groupIndex
          );

          const baseRequired = Math.max(
            1,
            Number.parseInt(
              element.dataset.baseRequired ||
                '1',
              10
            )
          );

          this.groups.set(index, {
            index,
            element,

            title:
              element.dataset.groupTitle ||
              `Grupo ${index}`,

            baseRequired,

            required:
              baseRequired * this.quantity,

            allowRepeats:
              element.dataset.allowRepeats ===
              'true',

            cards: Array.from(
              element.querySelectorAll(
                '[data-kenzu-card]'
              )
            )
          });

          this.selections.set(
            index,
            []
          );
        });
    }

    initializeCarousels() {
      this.root
        .querySelectorAll(
          '[data-kenzu-carousel]'
        )
        .forEach((carousel) => {
          const track =
            carousel.querySelector(
              '[data-kenzu-carousel-track]'
            );

          const previous =
            carousel.querySelector(
              '[data-kenzu-carousel-prev]'
            );

          const next =
            carousel.querySelector(
              '[data-kenzu-carousel-next]'
            );

          if (
            !track ||
            !previous ||
            !next
          ) {
            return;
          }

          const update = () => {
            const maximum =
              track.scrollWidth -
              track.clientWidth -
              2;

            previous.disabled =
              track.scrollLeft <= 2;

            next.disabled =
              track.scrollLeft >= maximum;
          };

          previous.addEventListener(
            'click',
            () => {
              track.scrollBy({
                left: -Math.max(
                  240,
                  track.clientWidth * 0.8
                ),
                behavior: 'smooth'
              });
            }
          );

          next.addEventListener(
            'click',
            () => {
              track.scrollBy({
                left: Math.max(
                  240,
                  track.clientWidth * 0.8
                ),
                behavior: 'smooth'
              });
            }
          );

          track.addEventListener(
            'scroll',
            update,
            {
              passive: true
            }
          );

          window.addEventListener(
            'resize',
            update,
            {
              passive: true
            }
          );

          requestAnimationFrame(update);
        });
    }

    bindEvents() {
      this.root.addEventListener(
        'click',
        (event) => {
          const toggle =
            event.target.closest(
              '[data-kenzu-card-toggle]'
            );

          const plus =
            event.target.closest(
              '[data-kenzu-card-plus]'
            );

          const minus =
            event.target.closest(
              '[data-kenzu-card-minus]'
            );

          const extraPlus =
            event.target.closest(
              '[data-kenzu-extra-plus]'
            );

          const extraMinus =
            event.target.closest(
              '[data-kenzu-extra-minus]'
            );

          const editSelection =
            event.target.closest(
              '[data-kenzu-edit-selection]'
            );

          const removeSelection =
            event.target.closest(
              '[data-kenzu-remove-selection]'
            );

          if (toggle) {
            this.addCardSelection(
              toggle.closest(
                '[data-kenzu-card]'
              )
            );

            return;
          }

          if (plus) {
            this.addCardSelection(
              plus.closest(
                '[data-kenzu-card]'
              )
            );

            return;
          }

          if (minus) {
            this.removeLastCardSelection(
              minus.closest(
                '[data-kenzu-card]'
              )
            );

            return;
          }

          if (extraPlus) {
            this.changeExtra(
              extraPlus.closest(
                '[data-kenzu-extra-row]'
              ),
              1
            );

            return;
          }

          if (extraMinus) {
            this.changeExtra(
              extraMinus.closest(
                '[data-kenzu-extra-row]'
              ),
              -1
            );

            return;
          }

          if (editSelection) {
            const selection =
              this.findSelectionById(
                editSelection.dataset
                  .kenzuEditSelection
              );

            if (selection) {
              this.openModal(selection);
            }

            return;
          }

          if (removeSelection) {
            this.removeSelectionById(
              removeSelection.dataset
                .kenzuRemoveSelection
            );
          }
        }
      );

      this.root
        .querySelector(
          '[data-kenzu-modal-close]'
        )
        ?.addEventListener(
          'click',
          () => {
            this.closeModal();
          }
        );

      this.root
        .querySelector(
          '[data-kenzu-modal-save]'
        )
        ?.addEventListener(
          'click',
          () => {
            this.closeModal();
            this.update();
          }
        );

      this.modal?.addEventListener(
        'click',
        (event) => {
          if (
            event.target === this.modal
          ) {
            this.closeModal();
          }
        }
      );

      ['input', 'change'].forEach(
        (eventName) => {
          this.quantityInput
            ?.addEventListener(
              eventName,
              () =>
                this.handleQuantityChange()
            );
        }
      );

      document.addEventListener(
        'submit',
        this.handleSubmitCapture,
        true
      );
    }

    handleQuantityChange() {
      const nextQuantity =
        getQuantity(
          this.quantityInput
        );

      if (
        nextQuantity === this.quantity
      ) {
        return;
      }

      this.quantity = nextQuantity;

      this.groups.forEach(
        (group) => {
          group.required =
            group.baseRequired *
            this.quantity;

          const selections =
            this.selections.get(
              group.index
            ) || [];

          if (
            selections.length >
            group.required
          ) {
            selections.splice(
              group.required
            );
          }
        }
      );

      this.update();
    }

    addCardSelection(card) {
      if (!card) return;

      const group =
        this.groups.get(
          String(
            card.dataset.groupIndex
          )
        );

      if (!group) return;

      const selections =
        this.selections.get(
          group.index
        ) || [];

      if (
        selections.length >=
        group.required
      ) {
        return;
      }

      const data = readJSON(
        card.querySelector(
          '[data-kenzu-card-data]'
        ),
        {}
      );

      if (
        !group.allowRepeats &&
        selections.some(
          (selection) =>
            selection.productId ===
            String(data.productId)
        )
      ) {
        return;
      }

      const initialVariant =
        data.variants?.find(
          (variant) =>
            String(variant.id) ===
            String(
              data.initialVariantId
            )
        ) ||
        data.variants?.find(
          (variant) =>
            variant.available !== false
        ) ||
        data.variants?.[0];

      const selectedOptions = {};

      (data.options || []).forEach(
        (option, index) => {
          selectedOptions[
            option.name
          ] =
            initialVariant
              ?.options?.[index] ||
            option.values?.[0] ||
            '';
        }
      );

      const selection = {
        id: [
          group.index,
          Date.now(),
          Math.random()
            .toString(16)
            .slice(2)
        ].join('-'),

        groupIndex:
          group.index,

        productId:
          String(data.productId),

        productTitle:
          data.productTitle,

        variantId:
          String(
            initialVariant?.id || ''
          ),

        variantTitle:
          initialVariant?.title ===
          'Default Title'
            ? ''
            : initialVariant?.title ||
              '',

        selectedOptions,

        replacements: [],

        notes: '',

        /*
         * NUEVOS ESTADOS.
         *
         * Sin nori:
         * 0 = normal
         * 1 = sin nori
         *
         * Cevichito:
         * cantidades independientes por selección.
         */
        withoutNoriQty: 0,

        cevichitoAcevicharQty: 0,

        cevichitoSeparateQty: 0,

        data
      };

      selections.push(selection);

      this.selections.set(
        group.index,
        selections
      );

      this.update();
      this.openModal(selection);
    }

    removeLastCardSelection(card) {
      if (!card) return;

      const group =
        this.groups.get(
          String(
            card.dataset.groupIndex
          )
        );

      if (!group) return;

      const productId = String(
        card.dataset.productId
      );

      const selections =
        this.selections.get(
          group.index
        ) || [];

      const index = selections
        .map(
          (selection) =>
            selection.productId
        )
        .lastIndexOf(productId);

      if (index >= 0) {
        selections.splice(
          index,
          1
        );
      }

      this.update();
    }

    findSelectionById(selectionId) {
      let found = null;

      this.selections.forEach(
        (items) => {
          const match = items.find(
            (selection) =>
              selection.id ===
              selectionId
          );

          if (match) {
            found = match;
          }
        }
      );

      return found;
    }

    removeSelectionById(
      selectionId
    ) {
      this.selections.forEach(
        (items, groupIndex) => {
          const filtered =
            items.filter(
              (selection) =>
                selection.id !==
                selectionId
            );

          this.selections.set(
            groupIndex,
            filtered
          );
        }
      );

      this.update();
    }

    openModal(selection) {
      if (
        !this.modal ||
        !this.modalBody ||
        !this.modalTitle
      ) {
        return;
      }

      this.currentSelection =
        selection;

      this.modalBody.innerHTML = '';

      this.modalTitle.textContent =
        selection.productTitle;

      this.modal
        .querySelector(
          '[data-kenzu-modal-description]'
        )
        ?.remove();

      if (
        selection.data
          .productDescriptionHtml
      ) {
        const description =
          document.createElement(
            'div'
          );

        description.className =
          'kenzu-modal__description rte';

        description.dataset
          .kenzuModalDescription = '';

        description.innerHTML =
          selection.data
            .productDescriptionHtml;

        this.modalTitle
          .insertAdjacentElement(
            'afterend',
            description
          );
      }

      /*
       * Opciones / proteína.
       */
      (
        selection.data.options || []
      ).forEach((option) => {
        this.modalBody.appendChild(
          this.createOptionField(
            selection,
            option
          )
        );
      });

      /*
       * Cambios de vegetal.
       */
      const replaceable = (
        selection.data
          .replaceableVegetables || []
      ).filter(
        (item) =>
          item.active !== false
      );

      const replacements = (
        selection.data
          .replacementVegetables || []
      ).filter(
        (item) =>
          item.active !== false
      );

      if (
        replaceable.length &&
        replacements.length
      ) {
        this.modalBody.appendChild(
          this.createMultiReplacementField(
            selection,
            replaceable,
            replacements
          )
        );
      }

      /*
       * NUEVO:
       * Sin nori solo aparece en rolls habilitados.
       */
      if (
        selection.data
          .allowWithoutNori === true &&
        selection.data
          .withoutNori
          ?.variantId
      ) {
        this.modalBody.appendChild(
          this.createWithoutNoriField(
            selection
          )
        );
      }

      /*
       * NUEVO:
       * Cevichito aparece si la promo tiene configurado
       * custom.cevichito_product.
       */
      if (
        selection.data
          .cevichito
          ?.variantId
      ) {
        this.modalBody.appendChild(
          this.createCevichitoField(
            selection
          )
        );
      }

      /*
       * Observaciones.
       */
      if (
        selection.data.allowNotes
      ) {
        this.modalBody.appendChild(
          this.createNotesField(
            selection
          )
        );
      }

      if (
        !this.modalBody.children.length
      ) {
        const message =
          document.createElement('p');

        message.textContent =
          'Este producto no tiene personalizaciones adicionales.';

        this.modalBody.appendChild(
          message
        );
      }

      const panel =
        this.modal.querySelector(
          '.kenzu-modal__panel'
        );

      if (panel) {
        panel.scrollTop = 0;
      }

      this.modal.classList.add(
        'is-open'
      );

      this.modal.setAttribute(
        'aria-hidden',
        'false'
      );

      document.body.classList.add(
        'kenzu-modal-open'
      );

      requestAnimationFrame(() => {
        this.modal
          .querySelector(
            '.kenzu-select, .kenzu-textarea, [data-kenzu-modal-save]'
          )
          ?.focus({
            preventScroll: true
          });
      });
    }

    closeModal() {
      if (!this.modal) return;

      this.modal.classList.remove(
        'is-open'
      );

      this.modal.setAttribute(
        'aria-hidden',
        'true'
      );

      this.currentSelection = null;

      document.body.classList.remove(
          'kenzu-modal-open'
      );

    }

    createOptionField(
      selection,
      option
    ) {
      const wrapper =
        document.createElement('div');

      wrapper.className =
        'kenzu-field';

      const label =
        document.createElement('label');

      label.textContent =
        option.name;

      const select =
        document.createElement(
          'select'
        );

      select.className =
        'kenzu-select';

      (option.values || []).forEach(
        (value) => {
          const element =
            document.createElement(
              'option'
            );

          const protein =
            this.proteinMap.get(
              normalize(value)
            );

          element.value = value;

          element.textContent =
            isProteinOption(
              option.name
            ) &&
            protein
              ?.surchargeVariantId
              ? `${value} — Recargo ${protein.formattedPrice}`
              : value;

          element.selected =
            selection
              .selectedOptions[
                option.name
              ] === value;

          select.appendChild(
            element
          );
        }
      );

      select.addEventListener(
        'change',
        () => {
          const previousValue =
            selection
              .selectedOptions[
                option.name
              ];

          selection
            .selectedOptions[
              option.name
            ] = select.value;

          const variant =
            this.resolveVariant(
              selection
            );

          if (!variant) {
            selection
              .selectedOptions[
                option.name
              ] = previousValue;

            select.value =
              previousValue;

            select.setCustomValidity(
              'Esta combinación no está disponible.'
            );

            select.reportValidity();

            return;
          }

          select.setCustomValidity('');

          selection.variantId =
            String(variant.id);

          selection.variantTitle =
            variant.title ===
            'Default Title'
              ? ''
              : variant.title;
        }
      );

      wrapper.append(
        label,
        select
      );

      return wrapper;
    }

    resolveVariant(selection) {
      const values = (
        selection.data.options || []
      ).map(
        (option) =>
          selection
            .selectedOptions[
              option.name
            ]
      );

      return (
        selection.data.variants || []
      ).find(
        (variant) =>
          variant.available !==
            false &&
          variant.options.every(
            (value, index) =>
              value ===
              values[index]
          )
      );
    }

    createMultiReplacementField(
      selection,
      replaceable,
      replacements
    ) {
      const fieldset =
        document.createElement(
          'fieldset'
        );

      fieldset.className =
        'kenzu-fieldset';

      const legend =
        document.createElement(
          'legend'
        );

      legend.textContent =
        'Cambios de vegetal';

      const grid =
        document.createElement('div');

      grid.className =
        'kenzu-vegetable-grid';

      replaceable.forEach(
        (original) => {
          const row =
            document.createElement(
              'div'
            );

          row.className =
            'kenzu-vegetable-row';

          const originalLabel =
            document.createElement(
              'span'
            );

          originalLabel.className =
            'kenzu-vegetable-row__original';

          originalLabel.textContent =
            original.name;

          const existing =
            selection.replacements.find(
              (replacement) =>
                replacement.from ===
                original.name
            );

          const select =
            this.createSelect(
              'Sin cambio',
              replacements,
              existing?.to || '',
              true
            );

          select.addEventListener(
            'change',
            () => {
              const destination =
                replacements.find(
                  (item) =>
                    item.name ===
                    select.value
                );

              selection.replacements =
                selection.replacements
                  .filter(
                    (replacement) =>
                      replacement.from !==
                      original.name
                  );

              if (
                select.value &&
                normalize(
                  select.value
                ) !==
                  normalize(
                    original.name
                  )
              ) {
                selection.replacements
                  .push({
                    from:
                      original.name,

                    to:
                      select.value,

                    surchargeVariantId:
                      destination
                        ?.surchargeVariantId ||
                      '',

                    formattedPrice:
                      destination
                        ?.formattedPrice ||
                      ''
                  });
              }

              if (
                select.value &&
                normalize(
                  select.value
                ) ===
                  normalize(
                    original.name
                  )
              ) {
                select.setCustomValidity(
                  'El vegetal nuevo debe ser diferente.'
                );
              } else {
                select.setCustomValidity(
                  ''
                );
              }
            }
          );

          row.append(
            originalLabel,
            select
          );

          grid.appendChild(row);
        }
      );

      fieldset.append(
        legend,
        grid
      );

      return fieldset;
    }

    createSelect(
      placeholder,
      options,
      selectedValue = '',
      showPrice = false
    ) {
      const select =
        document.createElement(
          'select'
        );

      select.className =
        'kenzu-select';

      const empty =
        document.createElement(
          'option'
        );

      empty.value = '';
      empty.textContent =
        placeholder;

      select.appendChild(empty);

      options.forEach((option) => {
        const element =
          document.createElement(
            'option'
          );

        element.value =
          option.name;

        element.textContent =
          showPrice &&
          option.surchargeVariantId
            ? `${option.name} — Recargo ${option.formattedPrice}`
            : option.name;

        element.selected =
          option.name ===
          selectedValue;

        select.appendChild(
          element
        );
      });

      return select;
    }

    /*
     * =======================================================
     * NUEVO — STEPPER REUTILIZABLE
     * =======================================================
     */
    createPreparationStepper({
      label,
      description = '',
      value = 0,
      min = 0,
      max = null,
      onChange
    }) {
      const row =
        document.createElement(
          'div'
        );

      row.className =
        'kenzu-preparation-row';

      const info =
        document.createElement(
          'div'
        );

      info.className =
        'kenzu-preparation-row__info';

      const title =
        document.createElement(
          'strong'
        );

      title.className =
        'kenzu-preparation-row__title';

      title.textContent =
        label;

      info.appendChild(title);

      if (description) {
        const text =
          document.createElement(
            'span'
          );

        text.className =
          'kenzu-preparation-row__description';

        text.textContent =
          description;

        info.appendChild(text);
      }

      const stepper =
        document.createElement(
          'div'
        );

      stepper.className =
        'kenzu-stepper';

      const minus =
        document.createElement(
          'button'
        );

      minus.type = 'button';
      minus.textContent = '−';

      minus.setAttribute(
        'aria-label',
        `Quitar ${label}`
      );

      const count =
        document.createElement(
          'span'
        );

      let current = Math.max(
        min,
        Number(value) || 0
      );

      if (max !== null) {
        current = Math.min(
          current,
          max
        );
      }

      count.textContent =
        String(current);

      const plus =
        document.createElement(
          'button'
        );

      plus.type = 'button';
      plus.textContent = '+';

      plus.setAttribute(
        'aria-label',
        `Añadir ${label}`
      );

      const updateButtons = () => {
        minus.disabled =
          current <= min;

        plus.disabled =
          max !== null &&
          current >= max;
      };

      const commit = () => {
        count.textContent =
          String(current);

        updateButtons();

        if (
          typeof onChange ===
          'function'
        ) {
          onChange(current);
        }
      };

      minus.addEventListener(
        'click',
        () => {
          if (current <= min) {
            return;
          }

          current -= 1;
          commit();
        }
      );

      plus.addEventListener(
        'click',
        () => {
          if (
            max !== null &&
            current >= max
          ) {
            return;
          }

          current += 1;
          commit();
        }
      );

      updateButtons();

      stepper.append(
        minus,
        count,
        plus
      );

      row.append(
        info,
        stepper
      );

      return row;
    }

    /*
     * =======================================================
     * NUEVO — SIN NORI
     * =======================================================
     */
    createWithoutNoriField(
      selection
    ) {
      const withoutNori =
        selection.data.withoutNori || {};

      const section =
        document.createElement(
          'section'
        );

      section.className =
        'kenzu-preparation';

      const heading =
        document.createElement(
          'h4'
        );

      heading.className =
        'kenzu-preparation__heading';

      heading.textContent =
        'Preparación';

      const row =
        this.createPreparationStepper({
          label:
            'Sin nori',

          description:
            withoutNori.formattedPrice
              ? `Recargo ${withoutNori.formattedPrice}`
              : 'Con recargo',

          value:
            selection.withoutNoriQty,

          min: 0,

          max: 1,

          onChange: (quantity) => {
            selection.withoutNoriQty =
              quantity;
          }
        });

      section.append(
        heading,
        row
      );

      return section;
    }

    /*
     * =======================================================
     * NUEVO — CEVICHITO KENZU
     * =======================================================
     */
    createCevichitoField(
      selection
    ) {
      const cevichito =
        selection.data.cevichito || {};

      const section =
        document.createElement(
          'section'
        );

      section.className =
        'kenzu-preparation kenzu-preparation--cevichito';

      const header =
        document.createElement(
          'div'
        );

      header.className =
        'kenzu-preparation__header';

      const heading =
        document.createElement(
          'h4'
        );

      heading.className =
        'kenzu-preparation__heading';

      heading.textContent =
        cevichito.productTitle ||
        'Cevichito Kenzu';

      const subtitle =
        document.createElement(
          'p'
        );

      subtitle.className =
        'kenzu-preparation__description';

      subtitle.textContent =
        cevichito.formattedPrice
          ? `Agregado opcional · ${cevichito.formattedPrice} c/u`
          : 'Agregado opcional';

      header.append(
        heading,
        subtitle
      );

      const list =
        document.createElement(
          'div'
        );

      list.className =
        'kenzu-preparation__list';

      const acevicharRow =
        this.createPreparationStepper({
          label:
            'Acevichar este roll',

          description:
            'Se asociará a este roll en el pedido.',

          value:
            selection
              .cevichitoAcevicharQty,

          min: 0,
          max: null,

          onChange: (quantity) => {
            selection
              .cevichitoAcevicharQty =
                quantity;
          }
        });

      const separateRow =
        this.createPreparationStepper({
          label:
            'Cevichito por separado',

          description:
            'Se agregará como producto global del pedido.',

          value:
            selection
              .cevichitoSeparateQty,

          min: 0,
          max: null,

          onChange: (quantity) => {
            selection
              .cevichitoSeparateQty =
                quantity;
          }
        });

      list.append(
        acevicharRow,
        separateRow
      );

      section.append(
        header,
        list
      );

      return section;
    }

    createNotesField(selection) {
      const wrapper =
        document.createElement(
          'div'
        );

      wrapper.className =
        'kenzu-field';

      const label =
        document.createElement(
          'label'
        );

      label.textContent =
        'Observaciones';

      const textarea =
        document.createElement(
          'textarea'
        );

      textarea.className =
        'kenzu-textarea';

      textarea.maxLength = 180;
      textarea.rows = 3;

      textarea.placeholder =
        'Ej.: salsa aparte';

      textarea.value =
        selection.notes || '';

      textarea.addEventListener(
        'input',
        () => {
          selection.notes =
            textarea.value.trim();
        }
      );

      wrapper.append(
        label,
        textarea
      );

      return wrapper;
    }
        changeExtra(row, change) {
      if (!row) return;

      const type = row.dataset.extraType;
      const name = row.dataset.extraName;

      const sequence =
        type === 'sauce'
          ? this.sauceSequence
          : this.condimentSequence;

      if (change > 0) {
        sequence.push(name);
      } else {
        const index =
          sequence.lastIndexOf(name);

        if (index >= 0) {
          sequence.splice(index, 1);
        }
      }

      this.updateExtras();
      this.updateSummary();
    }

    updateExtras() {
      /*
      * =========================================================
      * ACTUALIZAR CONTADORES DE CADA EXTRA
      * =========================================================
      */

      this.root
        .querySelectorAll(
          '[data-kenzu-extra-row]'
        )
        .forEach((row) => {
          const sequence =
            row.dataset.extraType ===
            'sauce'
              ? this.sauceSequence
              : this.condimentSequence;

          const quantity =
            sequence.filter(
              (name) =>
                name ===
                row.dataset.extraName
            ).length;

          const count =
            row.querySelector(
              '[data-kenzu-extra-count]'
            );

          if (count) {
            count.textContent =
              String(quantity);
          }
        });

      /*
      * =========================================================
      * ESTADO DE CADA SECCIÓN
      * =========================================================
      */

      this.root
        .querySelectorAll(
          '[data-kenzu-extra-section]'
        )
        .forEach((section) => {
          const type =
            section.dataset
              .extraSectionType;

          const isSauce =
            type === 'sauce';

          const sequence =
            isSauce
              ? this.sauceSequence
              : this.condimentSequence;

          const options =
            isSauce
              ? this.extraData?.sauces || []
              : this.extraData?.condiments || [];

          const baseLimit =
            Math.max(
              0,
              Number(
                isSauce
                  ? this.extraData
                      ?.freeSauces
                  : this.extraData
                      ?.freeCondiments
              ) || 0
            );

          /*
          * El límite incluido aumenta con
          * la cantidad de promociones.
          */
          const effectiveLimit =
            baseLimit *
            Math.max(
              1,
              Number(this.quantity) || 1
            );

          let eligibleCount = 0;
          let alwaysPaidCount = 0;

          /*
          * Separamos:
          *
          * eligibleCount:
          * extras que pueden utilizar los
          * cupos gratuitos.
          *
          * alwaysPaidCount:
          * salsas premium que siempre
          * generan recargo.
          */
          sequence.forEach((name) => {
            const option =
              options.find(
                (item) =>
                  item.name === name
              );

            if (!option) {
              return;
            }

            if (
              isSauce &&
              option.alwaysPaid
            ) {
              alwaysPaidCount += 1;
            } else {
              eligibleCount += 1;
            }
          });

          /*
          * Cuántas unidades gratuitas
          * todavía no fueron escogidas.
          */
          const missing =
            Math.max(
              0,
              effectiveLimit -
              eligibleCount
            );

          /*
          * Cuántas unidades exceden
          * el límite gratuito.
          */
          const exceeded =
            Math.max(
              0,
              eligibleCount -
              effectiveLimit
            );

          const limitElement =
            section.querySelector(
              '[data-kenzu-extra-effective-limit]'
            );

          const eligibleElement =
            section.querySelector(
              '[data-kenzu-extra-eligible-count]'
            );

          const paidElement =
            section.querySelector(
              '[data-kenzu-extra-always-paid]'
            );

          const warningElement =
            section.querySelector(
              '[data-kenzu-extra-warning]'
            );

          const status =
            section.querySelector(
              '[data-kenzu-extra-status]'
            );

          /*
          * Contadores visibles.
          */
          if (limitElement) {
            limitElement.textContent =
              String(effectiveLimit);
          }

          if (eligibleElement) {
            eligibleElement.textContent =
              String(eligibleCount);
          }

          /*
          * =====================================================
          * SALSAS PREMIUM
          * =====================================================
          */

          if (paidElement) {
            paidElement.hidden =
              alwaysPaidCount === 0;

            paidElement.textContent =
              alwaysPaidCount > 0
                ? `${alwaysPaidCount} salsa${
                    alwaysPaidCount === 1
                      ? ''
                      : 's'
                  } premium tendrá${
                    alwaysPaidCount === 1
                      ? ''
                      : 'n'
                  } recargo.`
                : '';
          }

          /*
          * =====================================================
          * MENSAJE PRINCIPAL
          * =====================================================
          *
          * Reutilizamos data-kenzu-extra-warning
          * para no modificar Liquid.
          */

          if (warningElement) {
            /*
            * CASO 1
            * Todavía faltan unidades incluidas.
            *
            * Esto es solamente informativo.
            * NO bloquea Add to Cart.
            */
            if (
              missing > 0 &&
              effectiveLimit > 0
            ) {
              warningElement.hidden =
                false;

              if (isSauce) {
                warningElement.textContent =
                  `Te falta${
                    missing === 1
                      ? ''
                      : 'n'
                  } ${missing} salsa${
                    missing === 1
                      ? ''
                      : 's'
                  } por escoger para completar las incluidas.`;
              } else {
                warningElement.textContent =
                  `Te falta${
                    missing === 1
                      ? ''
                      : 'n'
                  } ${missing} unidad${
                    missing === 1
                      ? ''
                      : 'es'
                  } por escoger de las incluidas.`;
              }
            }

            /*
            * CASO 2
            * Se completaron exactamente
            * todas las unidades gratuitas.
            */
            else if (
              effectiveLimit > 0 &&
              eligibleCount ===
                effectiveLimit
            ) {
              warningElement.hidden =
                false;

              warningElement.textContent =
                isSauce
                  ? '✓ Salsas incluidas completas'
                  : '✓ Unidades incluidas completas';
            }

            /*
            * CASO 3
            * Se excedió el límite.
            */
            else if (exceeded > 0) {
              warningElement.hidden =
                false;

              warningElement.textContent =
                `${exceeded} unidad${
                  exceeded === 1
                    ? ''
                    : 'es'
                } excede${
                  exceeded === 1
                    ? ''
                    : 'n'
                } el límite gratuito y tendrá${
                  exceeded === 1
                    ? ''
                    : 'n'
                } recargo.`;
            }

            /*
            * CASO 4
            * No existe límite gratuito.
            */
            else {
              warningElement.hidden =
                true;

              warningElement.textContent =
                '';
            }
          }

          /*
          * Estado visual rojo únicamente
          * cuando realmente existe un exceso.
          *
          * Las salsas faltantes NO se consideran
          * error.
          */
          status?.classList.toggle(
            'is-over-limit',
            exceeded > 0
          );

          /*
          * Clases adicionales para poder
          * estilizar después los estados.
          */
          status?.classList.toggle(
            'is-incomplete',
            missing > 0 &&
              effectiveLimit > 0
          );

          status?.classList.toggle(
            'is-complete',
            effectiveLimit > 0 &&
              eligibleCount ===
                effectiveLimit
          );
        });
    }

    update() {
      const promoQuantity =
        this.root.querySelector(
          '[data-kenzu-promo-quantity]'
        );

      if (promoQuantity) {
        promoQuantity.textContent =
          String(this.quantity);
      }

      this.groups.forEach((group) => {
        const selections =
          this.selections.get(group.index) || [];

        const current =
          selections.length;

        const percentage =
          Math.min(
            100,
            (current / group.required) * 100
          );

        group.element.classList.toggle(
          'is-complete',
          current === group.required
        );

        const currentElement =
          group.element.querySelector(
            '[data-kenzu-current]'
          );

        const requiredElement =
          group.element.querySelector(
            '[data-kenzu-required]'
          );

        const requiredText =
          group.element.querySelector(
            '[data-kenzu-required-text]'
          );

        const progress =
          group.element.querySelector(
            '[data-kenzu-progress]'
          );

        if (currentElement) {
          currentElement.textContent =
            current;
        }

        if (requiredElement) {
          requiredElement.textContent =
            group.required;
        }

        if (requiredText) {
          requiredText.textContent =
            group.required;
        }

        if (progress) {
          progress.style.width =
            `${percentage}%`;
        }

        group.cards.forEach((card) => {
          const productId =
            String(
              card.dataset.productId
            );

          const count =
            selections.filter(
              (selection) =>
                selection.productId ===
                productId
            ).length;

          const badge =
            card.querySelector(
              '[data-kenzu-card-badge]'
            );

          const toggle =
            card.querySelector(
              '[data-kenzu-card-toggle]'
            );

          const quantity =
            card.querySelector(
              '[data-kenzu-card-quantity]'
            );

          const quantityValue =
            card.querySelector(
              '[data-kenzu-card-count]'
            );

          card.classList.toggle(
            'is-selected',
            count > 0
          );

          if (badge) {
            badge.textContent = count;
          }

          if (quantityValue) {
            quantityValue.textContent = count;
          }

          if (group.allowRepeats) {
            if (toggle) {
              toggle.hidden =
                count > 0;

              toggle.disabled =
                selections.length >=
                group.required;
            }

            if (quantity) {
              quantity.hidden =
                count === 0;
            }
          } else {
            if (quantity) {
              quantity.hidden = true;
            }

            if (toggle) {
              toggle.hidden = false;

              toggle.textContent =
                count > 0
                  ? 'Seleccionado'
                  : 'Seleccionar';

              toggle.disabled =
                count > 0 ||
                selections.length >=
                  group.required;
            }
          }

          const plus =
            card.querySelector(
              '[data-kenzu-card-plus]'
            );

          if (plus) {
            plus.disabled =
              selections.length >=
              group.required;
          }
        });

        this.renderGroupSummary(group);
      });

      this.updateExtras();
      this.updateSummary();
      this.updateSubmit();
    }

    renderGroupSummary(group) {
      const selections =
        this.selections.get(group.index) || [];

      const content =
        group.element.querySelector(
          '[data-kenzu-group-summary-content]'
        );

      const label =
        group.element.querySelector(
          '[data-kenzu-group-summary-label]'
        );

      const currentElement =
        group.element.querySelector(
          '[data-kenzu-group-summary-current]'
        );

      const requiredElement =
        group.element.querySelector(
          '[data-kenzu-group-summary-required]'
        );

      if (currentElement) {
        currentElement.textContent =
          selections.length;
      }

      if (requiredElement) {
        requiredElement.textContent =
          group.required;
      }

      if (label) {
        label.textContent =
          selections.length ===
          group.required
            ? '✓ Elecciones completas'
            : 'Ver elecciones del grupo';
      }

      if (!content) return;

      content.innerHTML = '';

      if (!selections.length) {
        const empty =
          document.createElement('p');

        empty.className =
          'kenzu-group-summary__empty';

        empty.textContent =
          'Todavía no has elegido productos.';

        content.appendChild(empty);
        return;
      }

      selections.forEach(
        (selection, index) => {
          const item =
            document.createElement(
              'article'
            );

          item.className =
            'kenzu-group-choice';

          const info =
            document.createElement(
              'div'
            );

          const title =
            document.createElement(
              'strong'
            );

          title.className =
            'kenzu-group-choice__title';

          title.textContent =
            `${index + 1}. ${selection.productTitle}`;

          const details =
            document.createElement(
              'div'
            );

          details.className =
            'kenzu-group-choice__details';

          this.getDetailedSelectionLines(
            selection
          ).forEach((line) => {
            const detail =
              document.createElement(
                'span'
              );

            detail.textContent =
              line.text;

            if (
              line.hasSurcharge
            ) {
              detail.classList.add(
                'kenzu-group-choice__surcharge'
              );
            }

            details.appendChild(detail);
          });

          info.append(
            title,
            details
          );

          const actions =
            document.createElement(
              'div'
            );

          actions.className =
            'kenzu-group-choice__actions';

          const edit =
            document.createElement(
              'button'
            );

          edit.type = 'button';

          edit.className =
            'kenzu-group-choice__button';

          edit.dataset
            .kenzuEditSelection =
              selection.id;

          edit.textContent =
            'Editar';

          const remove =
            document.createElement(
              'button'
            );

          remove.type = 'button';

          remove.className =
            'kenzu-group-choice__button kenzu-group-choice__button--remove';

          remove.dataset
            .kenzuRemoveSelection =
              selection.id;

          remove.textContent =
            'Quitar';

          actions.append(
            edit,
            remove
          );

          item.append(
            info,
            actions
          );

          content.appendChild(item);
        }
      );
    }

    getDetailedSelectionLines(selection) {
      const lines = [];

      Object.entries(
        selection.selectedOptions || {}
      ).forEach(([name, value]) => {
        if (!value) return;

        const protein =
          isProteinOption(name)
            ? this.proteinMap.get(
                normalize(value)
              )
            : null;

        lines.push({
          text:
            protein?.surchargeVariantId
              ? `${name}: ${value} — Recargo ${protein.formattedPrice}`
              : `${name}: ${value}`,

          hasSurcharge:
            Boolean(
              protein?.surchargeVariantId
            )
        });
      });

      (
        selection.replacements || []
      ).forEach((replacement) => {
        lines.push({
          text:
            replacement
              .surchargeVariantId
              ? `${replacement.from} → ${replacement.to} — Recargo ${replacement.formattedPrice}`
              : `${replacement.from} → ${replacement.to}`,

          hasSurcharge:
            Boolean(
              replacement
                .surchargeVariantId
            )
        });
      });

      /*
       * NUEVO:
       * Sin nori en el resumen.
       */
      if (
        Number(
          selection.withoutNoriQty
        ) > 0
      ) {
        const price =
          selection.data
            .withoutNori
            ?.formattedPrice || '';

        lines.push({
          text:
            price
              ? `Sin nori — Recargo ${price}`
              : 'Sin nori',

          hasSurcharge: true
        });
      }

      /*
       * NUEVO:
       * Cevichito acevichado.
       */
      if (
        Number(
          selection
            .cevichitoAcevicharQty
        ) > 0
      ) {
        const quantity =
          Number(
            selection
              .cevichitoAcevicharQty
          );

        const price =
          selection.data
            .cevichito
            ?.formattedPrice || '';

        lines.push({
          text:
            price
              ? `Acevichar roll × ${quantity} — ${price} c/u`
              : `Acevichar roll × ${quantity}`,

          hasSurcharge: true
        });
      }

      /*
       * NUEVO:
       * Cevichito por separado.
       * En este paso todavía lo mostramos asociado
       * visualmente a la selección para que el cliente
       * pueda revisar lo que eligió.
       *
       * En carrito se agrupará globalmente.
       */
      if (
        Number(
          selection
            .cevichitoSeparateQty
        ) > 0
      ) {
        const quantity =
          Number(
            selection
              .cevichitoSeparateQty
          );

        const price =
          selection.data
            .cevichito
            ?.formattedPrice || '';

        lines.push({
          text:
            price
              ? `Cevichito por separado × ${quantity} — ${price} c/u`
              : `Cevichito por separado × ${quantity}`,

          hasSurcharge: true
        });
      }

      if (selection.notes) {
        lines.push({
          text:
            `Nota: ${selection.notes}`,

          hasSurcharge: false
        });
      }

      return lines;
    }

    updateSummary() {
      const container =
        this.root.querySelector(
          '[data-kenzu-summary-content]'
        );

      const empty =
        this.root.querySelector(
          '[data-kenzu-summary-empty]'
        );

      const currentElement =
        this.root.querySelector(
          '[data-kenzu-total-current]'
        );

      const requiredElement =
        this.root.querySelector(
          '[data-kenzu-total-required]'
        );

      const message =
        this.root.querySelector(
          '[data-kenzu-summary-message]'
        );

      const totalCurrent =
        Array.from(
          this.selections.values()
        ).reduce(
          (sum, items) =>
            sum + items.length,
          0
        );

      const totalRequired =
        Array.from(
          this.groups.values()
        ).reduce(
          (sum, group) =>
            sum + group.required,
          0
        );

      if (currentElement) {
        currentElement.textContent =
          totalCurrent;
      }

      if (requiredElement) {
        requiredElement.textContent =
          totalRequired;
      }

      if (empty) {
        empty.hidden =
          totalCurrent > 0 ||
          this.sauceSequence.length > 0 ||
          this.condimentSequence.length > 0;
      }

      if (message) {
        message.textContent =
          this.isComplete()
            ? 'Tu promoción está lista.'
            : this.missingMessage();
      }

      this.root.classList.toggle(
        'is-complete',
        this.isComplete()
      );

      if (!container) return;

      container.innerHTML = '';

      this.groups.forEach((group) => {
        const selections =
          this.selections.get(
            group.index
          ) || [];

        if (!selections.length) {
          return;
        }

        const section =
          document.createElement(
            'section'
          );

        section.className =
          'kenzu-summary__group';

        const heading =
          document.createElement(
            'h4'
          );

        heading.className =
          'kenzu-summary__group-title';

        const title =
          document.createElement(
            'span'
          );

        title.textContent =
          group.title;

        const count =
          document.createElement(
            'span'
          );

        count.textContent =
          `${selections.length}/${group.required}`;

        heading.append(
          title,
          count
        );

        const list =
          document.createElement(
            'ul'
          );

        list.className =
          'kenzu-summary__list';

        selections.forEach(
          (selection, index) => {
            const item =
              document.createElement(
                'li'
              );

            item.className =
              'kenzu-summary__item';

            item.textContent =
              `${index + 1}. ${selection.productTitle}`;

            const lines =
              this.getDetailedSelectionLines(
                selection
              );

            if (lines.length) {
              const meta =
                document.createElement(
                  'span'
                );

              meta.className =
                'kenzu-summary__item-meta';

              meta.textContent =
                lines
                  .map(
                    (line) =>
                      line.text
                  )
                  .join(' · ');

              item.appendChild(meta);
            }

            list.appendChild(item);
          }
        );

        section.append(
          heading,
          list
        );

        container.appendChild(
          section
        );
      });

      this.renderExtraSummary(
        container,
        'Salsas',
        this.sauceSequence
      );

      this.renderExtraSummary(
        container,
        'Wasabi y jengibre',
        this.condimentSequence
      );

      /*
       * NUEVO:
       * Cevichitos por separado se muestran también
       * como agregado global del pedido.
       */
      const separateCevichitoQuantity =
        this.getGlobalSeparateCevichitoQuantity();

      if (
        separateCevichitoQuantity > 0
      ) {
        this.renderQuantitySummary(
          container,
          'Cevichito Kenzu',
          separateCevichitoQuantity
        );
      }
    }

    /*
     * Suma todos los Cevichitos pedidos por separado
     * en las selecciones del Builder.
     */
    getGlobalSeparateCevichitoQuantity() {
      let total = 0;

      this.selections.forEach(
        (selections) => {
          selections.forEach(
            (selection) => {
              total += Math.max(
                0,
                Number(
                  selection
                    .cevichitoSeparateQty
                ) || 0
              );
            }
          );
        }
      );

      return total;
    }

    /*
     * Resumen sencillo para cantidades globales.
     */
    renderQuantitySummary(
      container,
      title,
      quantity
    ) {
      if (
        !container ||
        quantity <= 0
      ) {
        return;
      }

      const section =
        document.createElement(
          'section'
        );

      section.className =
        'kenzu-summary__group';

      const heading =
        document.createElement(
          'h4'
        );

      heading.className =
        'kenzu-summary__group-title';

      heading.textContent =
        'Agregados';

      const list =
        document.createElement(
          'ul'
        );

      list.className =
        'kenzu-summary__list';

      const item =
        document.createElement(
          'li'
        );

      item.className =
        'kenzu-summary__item';

      item.textContent =
        `${title} por separado × ${quantity}`;

      list.appendChild(item);

      section.append(
        heading,
        list
      );

      container.appendChild(section);
    }

    renderExtraSummary(
      container,
      title,
      sequence
    ) {
      if (!sequence.length) {
        return;
      }

      const counts =
        groupSequence(sequence);

      const section =
        document.createElement(
          'section'
        );

      section.className =
        'kenzu-summary__group';

      const heading =
        document.createElement(
          'h4'
        );

      heading.className =
        'kenzu-summary__group-title';

      heading.textContent =
        title;

      const list =
        document.createElement(
          'ul'
        );

      list.className =
        'kenzu-summary__list';

      counts.forEach(
        (quantity, name) => {
          const item =
            document.createElement(
              'li'
            );

          item.className =
            'kenzu-summary__item';

          item.textContent =
            `${name} × ${quantity}`;

          list.appendChild(item);
        }
      );

      section.append(
        heading,
        list
      );

      container.appendChild(
        section
      );
    }

    selectionDetails(selection) {
      return this
        .getDetailedSelectionLines(
          selection
        )
        .map(
          (line) => line.text
        );
    }

    isComplete() {
      return (
        this.groups.size > 0 &&
        Array.from(
          this.groups.values()
        ).every((group) => {
          return (
            (
              this.selections.get(
                group.index
              ) || []
            ).length ===
            group.required
          );
        })
      );
    }

    missingMessage() {
      const messages = [];

      this.groups.forEach((group) => {
        const current =
          (
            this.selections.get(
              group.index
            ) || []
          ).length;

        const missing =
          group.required -
          current;

        if (missing > 0) {
          messages.push(
            `${group.title}: faltan ${missing}`
          );
        }
      });

      return messages.join(' · ');
    }

    updateSubmit() {
      if (!this.submitButton) {
        return;
      }

      const enabled =
        this.isComplete() &&
        !this.isSubmitting;

      this.submitButton.disabled =
        !enabled;

      this.submitButton.setAttribute(
        'aria-disabled',
        enabled
          ? 'false'
          : 'true'
      );

      if (this.submitLabel) {
        this.submitLabel.textContent =
          this.isComplete()
            ? this.defaultSubmitText
            : 'Completa tu promoción';
      }
    }
    validateRequiredSauces() {
      /*
      * Esta validación es únicamente informativa.
      * Nunca bloquea el Add to Cart.
      */

      const freeSaucesPerPromo =
        Math.max(
          0,
          Number(
            this.extraData?.freeSauces
          ) || 0
        );

      /*
      * Si la promoción no incluye salsas gratis,
      * no hay nada que notificar.
      */
      if (freeSaucesPerPromo <= 0) {
        return true;
      }

      const requiredSauces =
        freeSaucesPerPromo *
        Math.max(
          1,
          Number(this.quantity) || 1
        );

      /*
      * Solo contamos salsas que pueden ocupar
      * los cupos gratuitos.
      *
      * Las alwaysPaid/premium no cuentan
      * como salsa gratis escogida.
      */
      let selectedFreeSauces = 0;

      (
        this.sauceSequence || []
      ).forEach((name) => {
        const option =
          (
            this.extraData?.sauces ||
            []
          ).find(
            (item) =>
              item.name === name
          );

        if (!option) {
          return;
        }

        if (option.alwaysPaid) {
          return;
        }

        selectedFreeSauces += 1;
      });

      const missing =
        Math.max(
          0,
          requiredSauces -
          selectedFreeSauces
        );

      /*
      * No bloqueamos el carrito.
      *
      * La función siempre devuelve true.
      * El estado visual de salsas continúa
      * manejándose mediante updateExtras().
      */
      if (missing > 0) {
        console.info(
          `Kenzu: quedan ${missing} salsa${
            missing === 1 ? '' : 's'
          } gratuita${
            missing === 1 ? '' : 's'
          } por escoger.`
        );
      }

      return true;
    }

    handleSubmitCapture(event) {
      if (
        event.target !== this.form
      ) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (
        !this.isComplete() ||
        this.isSubmitting
      ) {
        return;
      }

      /*
      * Solo informativa.
      * Nunca utilizamos su resultado
      * para bloquear el carrito.
      */
      this.validateRequiredSauces();

      this.addToCart();
    }

    buildSurchargeCounts() {
      const counts = new Map();

      /*
       * Aquí mantenemos SOLO los recargos globales
       * que ya funcionaban:
       *
       * - Proteínas
       * - Cambios de vegetal
       * - Salsas
       * - Wasabi / jengibre
       *
       * Sin nori NO se agrega aquí porque necesitamos
       * asociarlo al roll específico.
       */
      this.selections.forEach((selections) => {
        selections.forEach((selection) => {
          Object.entries(
            selection.selectedOptions || {}
          ).forEach(([optionName, value]) => {
            if (!isProteinOption(optionName)) {
              return;
            }

            const protein =
              this.proteinMap.get(
                normalize(value)
              );

            addCount(
              counts,
              protein?.surchargeVariantId,
              1
            );
          });

          (
            selection.replacements || []
          ).forEach((replacement) => {
            addCount(
              counts,
              replacement.surchargeVariantId,
              1
            );
          });
        });
      });

      this.addSauceSurcharges(counts);
      this.addCondimentSurcharges(counts);

      return counts;
    }

    addSauceSurcharges(counts) {
      let freeRemaining =
        Number(
          this.extraData.freeSauces || 0
        ) * this.quantity;

      this.sauceSequence.forEach((name) => {
        const option =
          this.extraData.sauces.find(
            (item) =>
              item.name === name
          );

        if (!option) return;

        if (option.alwaysPaid) {
          addCount(
            counts,
            option.surchargeVariantId,
            1
          );

          return;
        }

        if (freeRemaining > 0) {
          freeRemaining -= 1;
          return;
        }

        addCount(
          counts,
          option.surchargeVariantId,
          1
        );
      });
    }

    addCondimentSurcharges(counts) {
      let freeRemaining =
        Number(
          this.extraData.freeCondiments || 0
        ) * this.quantity;

      this.condimentSequence.forEach((name) => {
        const option =
          this.extraData.condiments.find(
            (item) =>
              item.name === name
          );

        if (!option) return;

        if (freeRemaining > 0) {
          freeRemaining -= 1;
          return;
        }

        addCount(
          counts,
          option.surchargeVariantId,
          1
        );
      });
    }

    /*
     * =========================================================
     * SIN NORI
     * =========================================================
     *
     * Cada selección representa un roll individual.
     *
     * Si ese roll tiene Sin nori = 1,
     * agregamos una línea del producto Recargos
     * asociada específicamente a esa selección.
     */
    appendWithoutNoriItems(items) {
      this.selections.forEach(
        (selections) => {
          selections.forEach(
            (selection) => {
              const variantId =
                Number(
                  selection.data
                    ?.withoutNori
                    ?.variantId
                );

              const quantity =
                Math.max(
                  0,
                  Number(
                    selection.withoutNoriQty
                  ) || 0
                );

              if (
                !Number.isFinite(variantId) ||
                variantId <= 0 ||
                quantity <= 0
              ) {
                return;
              }

              items.push({
                id: variantId,

                quantity,

                properties: {
                  'Tipo de recargo':
                    'Sin nori',

                  'Roll asociado a':
                    selection.productTitle
                }
              });
            }
          );
        }
      );
    }

    /*
     * =========================================================
     * CEVICHITO — ACEVICHAR ROLL
     * =========================================================
     *
     * Cada cantidad queda asociada al roll desde
     * cuyo editor fue escogida.
     *
     * Utilizamos el producto REAL Cevichito Kenzu.
     */
    appendAcevichadoItems(items) {
      this.selections.forEach(
        (selections) => {
          selections.forEach(
            (selection) => {
              const variantId =
                Number(
                  selection.data
                    ?.cevichito
                    ?.variantId
                );

              const quantity =
                Math.max(
                  0,
                  Number(
                    selection
                      .cevichitoAcevicharQty
                  ) || 0
                );

              if (
                !Number.isFinite(variantId) ||
                variantId <= 0 ||
                quantity <= 0
              ) {
                return;
              }

              items.push({
                id: variantId,

                quantity,

                properties: {
                  'Tipo':
                    'Acevichar roll',

                  'Roll asociado a':
                    selection.productTitle
                }
              });
            }
          );
        }
      );
    }

    /*
     * =========================================================
     * CEVICHITO — POR SEPARADO
     * =========================================================
     *
     * Todos los Cevichitos "por separado" escogidos
     * desde los distintos rolls se suman.
     *
     * Se agrega UNA sola línea global al carrito.
     *
     * No queda asociado a ningún roll.
     */
    appendSeparateCevichitoItem(
      items
    ) {
      /*
      * Agrupamos por variante porque el
      * Cevichito "por separado" es global.
      *
      * No queremos una línea distinta
      * por cada roll desde donde se escogió.
      */
      const quantitiesByVariant =
        new Map();

      this.selections.forEach(
        (selections) => {
          selections.forEach(
            (selection) => {
              const variantId =
                Number(
                  selection.data
                    ?.cevichito
                    ?.variantId
                );

              const quantity =
                Math.max(
                  0,
                  Number(
                    selection
                      .cevichitoSeparateQty
                  ) || 0
                );

              if (
                !Number.isFinite(
                  variantId
                ) ||
                variantId <= 0 ||
                quantity <= 0
              ) {
                return;
              }

              const key =
                String(
                  variantId
                );

              quantitiesByVariant.set(
                key,
                (
                  quantitiesByVariant.get(
                    key
                  ) || 0
                ) + quantity
              );
            }
          );
        }
      );

      quantitiesByVariant.forEach(
        (
          quantity,
          variantId
        ) => {
          const numericVariantId =
            Number(
              variantId
            );

          if (
            !Number.isFinite(
              numericVariantId
            ) ||
            numericVariantId <= 0 ||
            quantity <= 0
          ) {
            return;
          }

          /*
          * Una sola línea global.
          *
          * No lleva:
          * - selection id
          * - Roll asociado a
          *
          * porque debe quedar independiente
          * del roll.
          */
          items.push({
            id:
              numericVariantId,

            quantity,

            properties: {
      
              'Tipo':
                'Por separado'
            }
          });
        }
      );
    }

    buildProperties() {
      const properties = {};

      this.groups.forEach((group) => {
        const selections =
          this.selections.get(
            group.index
          ) || [];

        properties[
          `Promoción - ${group.title}`
        ] = selections
          .map(
            (selection, index) => {
              return [
                `${index + 1}. ${selection.productTitle}`,
                ...this.selectionDetails(
                  selection
                )
              ].join(' | ');
            }
          )
          .join(' || ');
      });

      if (
        this.sauceSequence.length
      ) {
        properties.Salsas =
          this.sauceSequence.join(', ');
      }

      if (
        this.condimentSequence.length
      ) {
        properties[
          'Wasabi y jengibre'
        ] =
          this.condimentSequence.join(
            ', '
          );
      }

      const separateCevichitoQuantity =
        this.getGlobalSeparateCevichitoQuantity();

      /*
       * Lo dejamos también registrado en la promo
       * para que el resumen interno del pedido
       * conserve la información completa.
       */
      if (
        separateCevichitoQuantity > 0
      ) {
        properties[
          'Cevichito Kenzu por separado'
        ] =
          `Cantidad: ${separateCevichitoQuantity}`;
      }


      return properties;
    }

    async addToCart() {
      this.isSubmitting = true;

      this.updateSubmit();

      setButtonLoading(
        this.submitButton,
        true
      );

      this.hideError();

      try {
        const parentVariantId =
          this.form.querySelector(
            '[name="id"]'
          )?.value;

        if (
          !parentVariantId ||
          !Number.isFinite(
            Number(parentVariantId)
          )
        ) {
          throw new Error(
            'No se encontró una variante válida para la promoción.'
          );
        }

        /*
        * =======================================================
        * 1. PROMOCIÓN PRINCIPAL
        * =======================================================
        */
        const items = [
          {
            id: Number(parentVariantId),

            quantity:
              this.quantity,

            properties:
              this.buildProperties()
          }
        ];

        /*
        * =======================================================
        * 2. RECARGOS EXISTENTES
        *
        * Proteínas
        * Vegetales
        * Salsas
        * Wasabi / jengibre
        * =======================================================
        */
        this.buildSurchargeCounts()
          .forEach(
            (
              quantity,
              variantId
            ) => {
              const numericVariantId =
                Number(variantId);

              if (
                !Number.isFinite(
                  numericVariantId
                ) ||
                numericVariantId <= 0 ||
                quantity <= 0
              ) {
                console.warn(
                  'Kenzu: recargo ignorado por datos inválidos.',
                  {
                    variantId,
                    quantity
                  }
                );

                return;
              }

              items.push({
                id:
                  numericVariantId,

                quantity,

                properties: {
                  'Recargo asociado a':
                    this.productTitle
                }
              });
            }
          );

        /*
        * =======================================================
        * 3. SIN NORI
        * =======================================================
        */
        this.appendWithoutNoriItems(
          items
        );

        /*
        * =======================================================
        * 4. CEVICHITO ACEVICHADO
        * =======================================================
        */
        this.appendAcevichadoItems(
          items
        );

        /*
        * =======================================================
        * 5. CEVICHITO POR SEPARADO
        * =======================================================
        */
        this.appendSeparateCevichitoItem(
          items
        );

        /*
        * =======================================================
        * VALIDACIÓN FINAL
        * =======================================================
        *
        * Esto nos protege de cualquier variante vacía,
        * null o NaN antes de llamar a Shopify.
        */
        const invalidItem =
          items.find(
            (item) =>
              !Number.isFinite(
                Number(item.id)
              ) ||
              Number(item.id) <= 0 ||
              !Number.isFinite(
                Number(item.quantity)
              ) ||
              Number(item.quantity) <= 0
          );

        if (invalidItem) {
          console.error(
            'Kenzu: línea de carrito inválida.',
            invalidItem
          );

          throw new Error(
            'Existe una personalización con una variante de cobro mal configurada.'
          );
        }

        /*
        * Muy útil mientras estamos terminando
        * esta integración.
        */
        console.log(
          'Kenzu — items enviados a Shopify:',
          items
        );

        await window.KenzuCart.addItems(
          items,
          'kenzu-builder',
          parentVariantId
        );

      } catch (error) {
        console.error(
          'Kenzu Builder:',
          error
        );

        this.showError(
          error?.message ||
          'No fue posible agregar la promoción al carrito.'
        );

      } finally {
        this.isSubmitting = false;

        setButtonLoading(
          this.submitButton,
          false
        );

        this.updateSubmit();
      }
    }

    showError(message) {
      const element =
        this.root.querySelector(
          '[data-kenzu-builder-error]'
        );

      if (!element) return;

      element.hidden = false;
      element.textContent =
        message;
    }

    hideError() {
      const element =
        this.root.querySelector(
          '[data-kenzu-builder-error]'
        );

      if (!element) return;

      element.hidden = true;
      element.textContent = '';
    }
  }

  /*
   * =========================================================
   * PERSONALIZADOR INDIVIDUAL
   *
   * SE MANTIENE INTACTO EN ESTE PASO.
   * =========================================================
   */

  class KenzuProductCustomizer {
    constructor(root) {
      this.root = root;

      this.form =
        document.getElementById(
          root.dataset.productFormId
        );

      this.quantityInput =
        document.querySelector(
          root.dataset.quantitySelector
        );

      this.productId =
        String(
          root.dataset.productId ||
            ''
        );

      this.productTitle =
        root.dataset.productTitle ||
        'Producto';

      this.data =
        readJSON(
          root.querySelector(
            '[data-kenzu-customizer-data]'
          ),
          {}
        );

      this.content =
        root.querySelector(
          '[data-kenzu-customizer-content]'
        );

      this.replacements = [];
      this.notes = '';

      /*
      * NUEVO — personalizaciones de preparación.
      */
      this.withoutNoriQty = 0;

      this.cevichitoAcevicharQty = 0;
      this.cevichitoSeparateQty = 0;

      this.sauceSequence = [];
      this.condimentSequence = [];

      this.extraSections =
        new Map();

      this.isSubmitting = false;

      this.submitButton =
        this.form?.querySelector(
          'button[type="submit"]'
        );

      this.handleSubmitCapture =
        this.handleSubmitCapture.bind(
          this
        );

      this.render();
      this.bindEvents();
      this.updateCustomizerExtras();
    }

    render() {
      if (!this.content) return;

      this.content.innerHTML = '';

      const replaceable = (
        this.data.replaceableVegetables || []
      ).filter(
        (item) =>
          item.active !== false
      );

      const replacements = (
        this.data.replacementVegetables || []
      ).filter(
        (item) =>
          item.active !== false
      );

      /*
      * Cambios de vegetal.
      */
      if (
        replaceable.length &&
        replacements.length
      ) {
        this.renderVegetableSection(
          replaceable,
          replacements
        );
      }

      /*
      * NUEVO — Sin nori.
      */
      if (
        this.data.allowWithoutNori === true
      ) {
        this.renderWithoutNoriSection();
      }

      /*
      * NUEVO — Cevichito Kenzu.
      */
      if (
        this.data.cevichito?.variantId
      ) {
        this.renderCevichitoSection();
      }

      /*
      * Observaciones.
      */
      if (this.data.allowNotes) {
        this.renderNotesSection();
      }

      /*
      * Salsas.
      */
      this.renderExtraSection(
        'Escoge tus salsas',
        'Puedes combinar las opciones gratuitas. Las premium se cobran siempre.',
        this.data.sauces || [],
        'sauce',
        Number(
          this.data.freeSauces || 0
        )
      );

      /*
      * Wasabi / jengibre.
      */
      this.renderExtraSection(
        'Wasabi y jengibre',
        'Puedes escoger unidades iguales o combinar ambas opciones.',
        this.data.condiments || [],
        'condiment',
        Number(
          this.data.freeCondiments || 0
        )
      );
    }

    createCustomizerPreparationStepper({
      label,
      description = '',
      value = 0,
      min = 0,
      max = null,
      onChange
    }) {
      const row =
        document.createElement('div');

      row.className =
        'kenzu-preparation-row';

      const info =
        document.createElement('div');

      info.className =
        'kenzu-preparation-row__info';

      const title =
        document.createElement('strong');

      title.className =
        'kenzu-preparation-row__title';

      title.textContent = label;

      info.appendChild(title);

      if (description) {
        const text =
          document.createElement('span');

        text.className =
          'kenzu-preparation-row__description';

        text.textContent =
          description;

        info.appendChild(text);
      }

      const stepper =
        document.createElement('div');

      stepper.className =
        'kenzu-stepper';

      const minus =
        document.createElement('button');

      minus.type = 'button';
      minus.textContent = '−';

      minus.setAttribute(
        'aria-label',
        `Quitar ${label}`
      );

      const count =
        document.createElement('span');

      let current = Math.max(
        min,
        Number(value) || 0
      );

      if (max !== null) {
        current = Math.min(
          current,
          max
        );
      }

      count.textContent =
        String(current);

      const plus =
        document.createElement('button');

      plus.type = 'button';
      plus.textContent = '+';

      plus.setAttribute(
        'aria-label',
        `Añadir ${label}`
      );

      const updateButtons = () => {
        minus.disabled =
          current <= min;

        plus.disabled =
          max !== null &&
          current >= max;
      };

      const commit = () => {
        count.textContent =
          String(current);

        updateButtons();

        if (
          typeof onChange ===
          'function'
        ) {
          onChange(current);
        }
      };

      minus.addEventListener(
        'click',
        () => {
          if (current <= min) {
            return;
          }

          current -= 1;
          commit();
        }
      );

      plus.addEventListener(
        'click',
        () => {
          if (
            max !== null &&
            current >= max
          ) {
            return;
          }

          current += 1;
          commit();
        }
      );

      updateButtons();

      stepper.append(
        minus,
        count,
        plus
      );

      row.append(
        info,
        stepper
      );

      return row;
    }

    renderWithoutNoriSection() {
      const section =
        document.createElement('section');

      section.className =
        'kenzu-customizer__section kenzu-preparation';

      const header =
        document.createElement('div');

      header.className =
        'kenzu-customizer__section-header';

      const headingWrapper =
        document.createElement('div');

      const heading =
        document.createElement('h4');

      heading.className =
        'kenzu-customizer__section-title';

      heading.textContent =
        'Preparación';

      const description =
        document.createElement('p');

      description.className =
        'kenzu-customizer__section-description';

      description.textContent =
        'Selecciona cuántos rolls quieres preparar sin nori.';

      headingWrapper.append(
        heading,
        description
      );

      header.appendChild(
        headingWrapper
      );

      const quantity =
        getQuantity(
          this.quantityInput
        );

      const row =
        this.createCustomizerPreparationStepper({
          label:
            'Sin nori',

          description:
            this.data.withoutNori?.formattedPrice
              ? `Recargo ${this.data.withoutNori.formattedPrice} c/u`
              : 'Con recargo',

          value:
            this.withoutNoriQty,

          min: 0,

          max:
            quantity,

          onChange: (value) => {
            this.withoutNoriQty =
              value;
          }
        });

      section.append(
        header,
        row
      );

      this.content.appendChild(
        section
      );
    }

    renderCevichitoSection() {
      const cevichito =
        this.data.cevichito || {};

      const section =
        document.createElement('section');

      section.className =
        'kenzu-customizer__section kenzu-preparation kenzu-preparation--cevichito';

      const header =
        document.createElement('div');

      header.className =
        'kenzu-customizer__section-header';

      const headingWrapper =
        document.createElement('div');

      const heading =
        document.createElement('h4');

      heading.className =
        'kenzu-customizer__section-title';

      heading.textContent =
        cevichito.productTitle ||
        'Cevichito Kenzu';

      const description =
        document.createElement('p');

      description.className =
        'kenzu-customizer__section-description';

      description.textContent =
        cevichito.formattedPrice
          ? `Agregado opcional · ${cevichito.formattedPrice} c/u`
          : 'Agregado opcional';

      headingWrapper.append(
        heading,
        description
      );

      header.appendChild(
        headingWrapper
      );

      const list =
        document.createElement('div');

      list.className =
        'kenzu-preparation__list';

      const acevichar =
        this.createCustomizerPreparationStepper({
          label:
            'Acevichar roll',

          description:
            'Se prepara el roll con Cevichito Kenzu.',

          value:
            this.cevichitoAcevicharQty,

          min: 0,

          max: null,

          onChange: (value) => {
            this.cevichitoAcevicharQty =
              value;
          }
        });

      const separate =
        this.createCustomizerPreparationStepper({
          label:
            'Cevichito por separado',

          description:
            'Se agrega aparte al pedido.',

          value:
            this.cevichitoSeparateQty,

          min: 0,

          max: null,

          onChange: (value) => {
            this.cevichitoSeparateQty =
              value;
          }
        });

      list.append(
        acevichar,
        separate
      );

      section.append(
        header,
        list
      );

      this.content.appendChild(
        section
      );
    }

    renderVegetableSection(
      replaceable,
      replacements
    ) {
      const section =
        document.createElement(
          'section'
        );

      section.className =
        'kenzu-customizer__section';

      const header =
        document.createElement(
          'div'
        );

      header.className =
        'kenzu-customizer__section-header';

      const headingWrapper =
        document.createElement(
          'div'
        );

      const heading =
        document.createElement(
          'h4'
        );

      heading.className =
        'kenzu-customizer__section-title';

      heading.textContent =
        'Cambios de vegetal';

      const description =
        document.createElement(
          'p'
        );

      description.className =
        'kenzu-customizer__section-description';

      description.textContent =
        'Puedes modificar cada vegetal por separado.';

      headingWrapper.append(
        heading,
        description
      );

      header.appendChild(
        headingWrapper
      );

      const grid =
        document.createElement(
          'div'
        );

      grid.className =
        'kenzu-vegetable-grid';

      replaceable.forEach(
        (original) => {
          const row =
            document.createElement(
              'div'
            );

          row.className =
            'kenzu-vegetable-row';

          const originalLabel =
            document.createElement(
              'span'
            );

          originalLabel.className =
            'kenzu-vegetable-row__original';

          originalLabel.textContent =
            original.name;

          const select =
            this.createSelect(
              'Sin cambio',
              replacements,
              true
            );

          select.addEventListener(
            'change',
            () => {
              this.replacements =
                this.replacements.filter(
                  (replacement) =>
                    replacement.from !==
                    original.name
                );

              const destination =
                replacements.find(
                  (item) =>
                    item.name ===
                    select.value
                );

              if (
                select.value &&
                normalize(
                  select.value
                ) !==
                  normalize(
                    original.name
                  )
              ) {
                this.replacements.push({
                  from:
                    original.name,

                  to:
                    select.value,

                  surchargeVariantId:
                    destination
                      ?.surchargeVariantId ||
                    '',

                  formattedPrice:
                    destination
                      ?.formattedPrice ||
                    ''
                });

                select.setCustomValidity(
                  ''
                );
              } else if (
                select.value
              ) {
                select.setCustomValidity(
                  'El vegetal nuevo debe ser diferente.'
                );
              } else {
                select.setCustomValidity(
                  ''
                );
              }
            }
          );

          row.append(
            originalLabel,
            select
          );

          grid.appendChild(
            row
          );
        }
      );

      section.append(
        header,
        grid
      );

      this.content.appendChild(
        section
      );
    }

    renderNotesSection() {
      const section =
        document.createElement(
          'section'
        );

      section.className =
        'kenzu-customizer__section';

      const field =
        document.createElement(
          'div'
        );

      field.className =
        'kenzu-field';

      const label =
        document.createElement(
          'label'
        );

      label.textContent =
        'Observaciones';

      const textarea =
        document.createElement(
          'textarea'
        );

      textarea.className =
        'kenzu-textarea';

      textarea.rows = 3;
      textarea.maxLength = 180;

      textarea.placeholder =
        'Ej.: salsa aparte';

      textarea.addEventListener(
        'input',
        () => {
          this.notes =
            textarea.value.trim();
        }
      );

      field.append(
        label,
        textarea
      );

      section.appendChild(
        field
      );

      this.content.appendChild(
        section
      );
    }

    createSelect(
      placeholder,
      options,
      showPrice = false
    ) {
      const select =
        document.createElement(
          'select'
        );

      select.className =
        'kenzu-select';

      const empty =
        document.createElement(
          'option'
        );

      empty.value = '';
      empty.textContent =
        placeholder;

      select.appendChild(
        empty
      );

      options.forEach(
        (option) => {
          const element =
            document.createElement(
              'option'
            );

          element.value =
            option.name;

          element.textContent =
            showPrice &&
            option.surchargeVariantId
              ? `${option.name} — Recargo ${option.formattedPrice}`
              : option.name;

          select.appendChild(
            element
          );
        }
      );

      return select;
    }

    renderExtraSection(
      title,
      descriptionText,
      options,
      type,
      baseFreeLimit
    ) {
      const activeOptions =
        options.filter(
          (item) =>
            item.active !== false
        );

      if (
        !activeOptions.length
      ) {
        return;
      }

      const section =
        document.createElement(
          'section'
        );

      section.className =
        'kenzu-customizer__section';

      section.dataset
        .customizerExtraType =
          type;

      const header =
        document.createElement(
          'div'
        );

      header.className =
        'kenzu-customizer__section-header';

      const headingWrapper =
        document.createElement(
          'div'
        );

      const heading =
        document.createElement(
          'h4'
        );

      heading.className =
        'kenzu-customizer__section-title';

      heading.textContent =
        title;

      const description =
        document.createElement(
          'p'
        );

      description.className =
        'kenzu-customizer__section-description';

      description.textContent =
        descriptionText;

      headingWrapper.append(
        heading,
        description
      );

      const included =
        document.createElement(
          'span'
        );

      included.className =
        'kenzu-customizer__included';

      included.innerHTML =
        `<strong data-customizer-base-limit>${baseFreeLimit}</strong> gratis por roll`;

      header.append(
        headingWrapper,
        included
      );

      const list =
        document.createElement(
          'div'
        );

      list.className =
        'kenzu-extra-list';

      activeOptions.forEach(
        (option) => {
          const row =
            document.createElement(
              'div'
            );

          row.className =
            'kenzu-extra-row';

          const info =
            document.createElement(
              'div'
            );

          info.className =
            'kenzu-extra-row__info';

          const optionTitle =
            document.createElement(
              'span'
            );

          optionTitle.className =
            'kenzu-extra-row__title';

          optionTitle.textContent =
            option.name;

          const price =
            document.createElement(
              'span'
            );

          price.className =
            'kenzu-extra-row__price';

          if (
            type === 'sauce' &&
            option.alwaysPaid
          ) {
            price.textContent =
              `Siempre se cobra${
                option.formattedPrice
                  ? ` · ${option.formattedPrice}`
                  : ''
              }`;
          } else {
            price.textContent =
              option.surchargeVariantId
                ? `Gratis dentro del límite · Extra ${option.formattedPrice}`
                : 'Gratis dentro del límite';
          }

          info.append(
            optionTitle,
            price
          );

          const stepper =
            document.createElement(
              'div'
            );

          stepper.className =
            'kenzu-stepper';

          const minus =
            document.createElement(
              'button'
            );

          minus.type = 'button';
          minus.textContent = '−';

          const count =
            document.createElement(
              'span'
            );

          count.textContent = '0';

          const plus =
            document.createElement(
              'button'
            );

          plus.type = 'button';
          plus.textContent = '+';

          minus.addEventListener(
            'click',
            () => {
              this.changeCustomizerExtra(
                type,
                option.name,
                -1
              );

              count.textContent =
                this.getExtraCount(
                  type,
                  option.name
                );

              this.updateCustomizerExtras();
            }
          );

          plus.addEventListener(
            'click',
            () => {
              this.changeCustomizerExtra(
                type,
                option.name,
                1
              );

              count.textContent =
                this.getExtraCount(
                  type,
                  option.name
                );

              this.updateCustomizerExtras();
            }
          );

          stepper.append(
            minus,
            count,
            plus
          );

          row.append(
            info,
            stepper
          );

          list.appendChild(
            row
          );
        }
      );

      const status =
        document.createElement(
          'div'
        );

      status.className =
        'kenzu-extra-status';

      status.dataset
        .customizerExtraStatus =
          type;

      const main =
        document.createElement(
          'span'
        );

      main.innerHTML =
        `${
          type === 'sauce'
            ? 'Salsas gratuitas escogidas'
            : 'Wasabi y jengibre escogidos'
        }: <strong data-customizer-eligible-count>0</strong> / <strong data-customizer-effective-limit>${baseFreeLimit}</strong>`;

      const paid =
        document.createElement(
          'span'
        );

      paid.className =
        'kenzu-extra-status__paid';

      paid.dataset
        .customizerAlwaysPaid = '';

      paid.hidden = true;

      const warning =
        document.createElement(
          'span'
        );

      warning.className =
        'kenzu-extra-status__warning';

      warning.dataset
        .customizerWarning = '';

      warning.hidden = true;

      status.append(
        main,
        paid,
        warning
      );

      section.append(
        header,
        list,
        status
      );

      this.extraSections.set(
        type,
        {
          element: section,
          baseFreeLimit,
          options:
            activeOptions
        }
      );

      this.content.appendChild(
        section
      );
    }

    bindEvents() {
      ['input', 'change']
        .forEach(
          (eventName) => {
            this.quantityInput
              ?.addEventListener(
                eventName,
                () =>
                  this.updateCustomizerExtras()
              );
          }
        );

      document.addEventListener(
        'submit',
        this.handleSubmitCapture,
        true
      );
    }

    changeCustomizerExtra(
      type,
      name,
      change
    ) {
      const sequence =
        type === 'sauce'
          ? this.sauceSequence
          : this.condimentSequence;

      if (change > 0) {
        sequence.push(name);
      } else {
        const index =
          sequence.lastIndexOf(
            name
          );

        if (index >= 0) {
          sequence.splice(
            index,
            1
          );
        }
      }
    }

    getExtraCount(
      type,
      name
    ) {
      const sequence =
        type === 'sauce'
          ? this.sauceSequence
          : this.condimentSequence;

      return sequence.filter(
        (entry) =>
          entry === name
      ).length;
    }

    updateCustomizerExtras() {
      const quantity =
        getQuantity(
          this.quantityInput
        );

      this.extraSections.forEach(
        (
          configuration,
          type
        ) => {
          const sequence =
            type === 'sauce'
              ? this.sauceSequence
              : this.condimentSequence;

          const effectiveLimit =
            configuration
              .baseFreeLimit *
            quantity;

          let eligibleCount = 0;
          let alwaysPaidCount = 0;

          sequence.forEach(
            (name) => {
              const option =
                configuration
                  .options.find(
                    (item) =>
                      item.name ===
                      name
                  );

              if (!option) {
                return;
              }

              if (
                type === 'sauce' &&
                option.alwaysPaid
              ) {
                alwaysPaidCount +=
                  1;
              } else {
                eligibleCount +=
                  1;
              }
            }
          );

          const exceeded =
            Math.max(
              0,
              eligibleCount -
                effectiveLimit
            );

          const missing = 
            Math.max(
              0,
              effectiveLimit - eligibleCount
            );

          const status =
            configuration
              .element
              .querySelector(
                '[data-customizer-extra-status]'
              );

          const eligible =
            configuration
              .element
              .querySelector(
                '[data-customizer-eligible-count]'
              );

          const limit =
            configuration
              .element
              .querySelector(
                '[data-customizer-effective-limit]'
              );

          const paid =
            configuration
              .element
              .querySelector(
                '[data-customizer-always-paid]'
              );

          const warning =
            configuration
              .element
              .querySelector(
                '[data-customizer-warning]'
              );

          if (eligible) {
            eligible.textContent =
              String(
                eligibleCount
              );
          }

          if (limit) {
            limit.textContent =
              String(
                effectiveLimit
              );
          }

          if (paid) {
            paid.hidden =
              alwaysPaidCount === 0;

            paid.textContent =
              alwaysPaidCount > 0
                ? `${alwaysPaidCount} salsa${
                    alwaysPaidCount ===
                    1
                      ? ''
                      : 's'
                  } premium tendrá${
                    alwaysPaidCount ===
                    1
                      ? ''
                      : 'n'
                  } recargo.`
                : '';
          }

          if (warning) {
            if (missing > 0) {
              warning.hidden = false;

              warning.textContent =
                `Te faltan ${missing} salsa${
                  missing === 1 ? '' : 's'
                } por escoger para completar las incluidas.`;

            } else if (exceeded > 0) {
              warning.hidden = false;

              warning.textContent =
                `${exceeded} unidad${
                  exceeded === 1 ? '' : 'es'
                } excede${
                  exceeded === 1 ? '' : 'n'
                } el límite gratuito y tendrá${
                  exceeded === 1 ? '' : 'n'
                } recargo.`;

            } else {
              warning.hidden = true;
              warning.textContent = '';
            }
          }

          status?.classList.toggle(
            'is-over-limit',
            missing > 0 ||
            exceeded > 0
          );
        }
      );
    }

    handleSubmitCapture(
      event
    ) {
      if (
        event.target !==
        this.form
      ) {
        return;
      }

      event.preventDefault();

      event.stopImmediatePropagation();

      if (
        this.isSubmitting
      ) {
        return;
      }

      this.addToCart();
    }

    buildSurcharges(quantity) {
      const counts = new Map();

      /*
      * =========================================================
      * CAMBIOS DE VEGETAL
      * =========================================================
      */
      this.replacements.forEach(
        (replacement) => {
          addCount(
            counts,
            replacement.surchargeVariantId,
            quantity
          );
        }
      );

      /*
      * =========================================================
      * SALSAS
      * =========================================================
      */
      let freeSauces =
        Number(
          this.data.freeSauces || 0
        ) * quantity;

      this.sauceSequence.forEach(
        (name) => {
          const option =
            (this.data.sauces || []).find(
              (item) =>
                item.name === name
            );

          if (!option) return;

          /*
          * Salsa premium:
          * siempre cobra.
          */
          if (option.alwaysPaid) {
            addCount(
              counts,
              option.surchargeVariantId,
              1
            );

            return;
          }

          /*
          * Salsa dentro del límite gratuito.
          */
          if (freeSauces > 0) {
            freeSauces -= 1;
            return;
          }

          /*
          * Salsa adicional sobre el límite.
          */
          addCount(
            counts,
            option.surchargeVariantId,
            1
          );
        }
      );

      /*
      * =========================================================
      * WASABI / JENGIBRE
      * =========================================================
      */
      let freeCondiments =
        Number(
          this.data.freeCondiments || 0
        ) * quantity;

      this.condimentSequence.forEach(
        (name) => {
          const option =
            (this.data.condiments || []).find(
              (item) =>
                item.name === name
            );

          if (!option) return;

          if (freeCondiments > 0) {
            freeCondiments -= 1;
            return;
          }

          addCount(
            counts,
            option.surchargeVariantId,
            1
          );
        }
      );

      return counts;
    }


    /*
    * =========================================================
    * SIN NORI
    * =========================================================
    *
    * Sin nori utiliza el producto/variante de recargo $1.000.
    *
    * Se agrega como línea independiente para que quede
    * claramente asociada al roll.
    */
    appendCustomizerWithoutNoriItem(
      items,
      quantity
    ) {
      const withoutNoriQuantity =
        Math.max(
          0,
          Number(
            this.withoutNoriQty
          ) || 0
        );

      if (
        withoutNoriQuantity <= 0
      ) {
        return;
      }

      /*
      * Seguridad:
      * no permitimos cobrar Sin nori si Shopify no nos
      * entregó la variante correspondiente.
      */
      const variantId =
        this.data.withoutNori
          ?.variantId;

      if (!variantId) {
        throw new Error(
          'Sin nori está seleccionado, pero no se encontró su variante de recargo.'
        );
      }

      /*
      * Sin nori no puede superar la cantidad de rolls
      * comprados en la PDP individual.
      */
      if (
        withoutNoriQuantity >
        quantity
      ) {
        throw new Error(
          'La cantidad de rolls sin nori no puede superar la cantidad de rolls del pedido.'
        );
      }

      items.push({
        id:
          Number(
            variantId
          ),

        quantity:
          withoutNoriQuantity,

        properties: {
          '_Kenzu surcharge':
            'true',

          '_Kenzu parent product':
            this.productId,

          'Tipo de recargo':
            'Sin nori',

          'Roll asociado a':
            this.productTitle
        }
      });
    }


    /*
    * =========================================================
    * CEVICHITO — ACEVICHAR
    * =========================================================
    *
    * Producto Cevichito Kenzu real.
    * Queda asociado al roll de esta PDP.
    */
    appendCustomizerAcevichadoItem(
      items
    ) {
      const quantity =
        Math.max(
          0,
          Number(
            this.cevichitoAcevicharQty
          ) || 0
        );

      if (quantity <= 0) {
        return;
      }

      const variantId =
        this.data.cevichito
          ?.variantId;

      if (!variantId) {
        throw new Error(
          'No se encontró la variante de Cevichito Kenzu.'
        );
      }

      items.push({
        id:
          Number(
            variantId
          ),

        quantity,

        properties: {
          '_Kenzu parent product':
            this.productId,

          'Tipo':
            'Acevichar roll',

          'Roll asociado a':
            this.productTitle
        }
      });
    }


    /*
    * =========================================================
    * CEVICHITO — POR SEPARADO
    * =========================================================
    *
    * Producto independiente/global.
    * No queda asociado al roll.
    */
    appendCustomizerSeparateCevichitoItem(
      items
    ) {
      const quantity =
        Math.max(
          0,
          Number(
            this.cevichitoSeparateQty
          ) || 0
        );

      if (quantity <= 0) {
        return;
      }

      const variantId =
        this.data.cevichito
          ?.variantId;

      if (!variantId) {
        throw new Error(
          'No se encontró la variante de Cevichito Kenzu.'
        );
      }

      items.push({
        id:
          Number(
            variantId
          ),

        quantity,

        properties: {
          'Tipo':
            'Por separado'
        }
      });
    }


    async addToCart() {
      this.isSubmitting = true;

      setButtonLoading(
        this.submitButton,
        true
      );

      this.hideError();

      try {
        const variantId =
          this.form.querySelector(
            '[name="id"]'
          )?.value;

        if (!variantId) {
          throw new Error(
            'No se encontró la variante seleccionada.'
          );
        }

        const quantity =
          getQuantity(
            this.quantityInput
          );

        /*
        * =======================================================
        * PROPIEDADES DEL ROLL PRINCIPAL
        * =======================================================
        */
        const properties = {};

        if (
          this.replacements.length
        ) {
          properties[
            'Cambios de vegetal'
          ] =
            this.replacements
              .map(
                (replacement) =>
                  `${replacement.from} → ${replacement.to}`
              )
              .join(' | ');
        }

        if (
          Number(
            this.withoutNoriQty
          ) > 0
        ) {
          properties[
            'Sin nori'
          ] =
            `Cantidad: ${this.withoutNoriQty}`;
        }

        if (
          Number(
            this.cevichitoAcevicharQty
          ) > 0
        ) {
          properties[
            'Acevichado con Cevichito Kenzu'
          ] =
            `Cantidad: ${this.cevichitoAcevicharQty}`;
        }

        /*
        * No registramos Cevichito por separado dentro
        * del roll principal porque conceptualmente es
        * un agregado global independiente.
        */

        if (this.notes) {
          properties.Observaciones =
            this.notes;
        }

        if (
          this.sauceSequence.length
        ) {
          properties.Salsas =
            this.sauceSequence.join(
              ', '
            );
        }

        if (
          this.condimentSequence.length
        ) {
          properties[
            'Wasabi y jengibre'
          ] =
            this.condimentSequence.join(
              ', '
            );
        }

        /*
        * =======================================================
        * 1. ROLL PRINCIPAL
        * =======================================================
        */
        const items = [
          {
            id:
              Number(
                variantId
              ),

            quantity,

            properties
          }
        ];

        /*
        * =======================================================
        * 2. RECARGOS EXISTENTES
        *
        * Vegetales
        * Salsas
        * Wasabi / jengibre
        * =======================================================
        */
        this.buildSurcharges(
          quantity
        ).forEach(
          (
            surchargeQuantity,
            surchargeVariantId
          ) => {
            items.push({
              id:
                Number(
                  surchargeVariantId
                ),

              quantity:
                surchargeQuantity,

              properties: {
                '_Kenzu surcharge':
                  'true',

                '_Kenzu parent product':
                  this.productId,

                'Recargo asociado a':
                  this.productTitle
              }
            });
          }
        );

        /*
        * =======================================================
        * 3. SIN NORI
        * =======================================================
        */
        this.appendCustomizerWithoutNoriItem(
          items,
          quantity
        );

        /*
        * =======================================================
        * 4. CEVICHITO ACEVICHADO
        * =======================================================
        */
        this.appendCustomizerAcevichadoItem(
          items
        );

        /*
        * =======================================================
        * 5. CEVICHITO POR SEPARADO
        * =======================================================
        */
        this.appendCustomizerSeparateCevichitoItem(
          items
        );

        /*
        * =======================================================
        * ENVÍO AL CARRITO
        * =======================================================
        */
        await window.KenzuCart.addItems(
          items,
          'kenzu-customizer',
          variantId
        );

      } catch (error) {
        console.error(
          'Kenzu Customizer:',
          error
        );

        this.showError(
          error.message
        );

      } finally {
        this.isSubmitting =
          false;

        setButtonLoading(
          this.submitButton,
          false
        );
      }
    }

    showError(message) {
      const element =
        this.root.querySelector(
          '[data-kenzu-customizer-error]'
        );

      if (!element) return;

      element.hidden = false;

      element.textContent =
        message;
    }

    hideError() {
      const element =
        this.root.querySelector(
          '[data-kenzu-customizer-error]'
        );

      if (!element) return;

      element.hidden = true;
      element.textContent = '';
    }
  }

  /*
   * =========================================================
   * INICIALIZACIÓN
   * =========================================================
   */

  function initialize(
    scope = document
  ) {
    const builders = [];
    const customizers = [];

    if (
      scope.matches?.(
        '[data-kenzu-builder]'
      )
    ) {
      builders.push(scope);
    }

    if (
      scope.matches?.(
        '[data-kenzu-customizer]'
      )
    ) {
      customizers.push(scope);
    }

    scope
      .querySelectorAll?.(
        '[data-kenzu-builder]'
      )
      .forEach(
        (element) =>
          builders.push(
            element
          )
      );

    scope
      .querySelectorAll?.(
        '[data-kenzu-customizer]'
      )
      .forEach(
        (element) =>
          customizers.push(
            element
          )
      );

    builders.forEach(
      (element) => {
        if (
          element.dataset
            .kenzuInitialized ===
          'true'
        ) {
          return;
        }

        element.dataset
          .kenzuInitialized =
            'true';

        element.kenzuBuilder =
          new KenzuPromoBuilder(
            element
          );
      }
    );

    customizers.forEach(
      (element) => {
        if (
          element.dataset
            .kenzuInitialized ===
          'true'
        ) {
          return;
        }

        element.dataset
          .kenzuInitialized =
            'true';

        element.kenzuCustomizer =
          new KenzuProductCustomizer(
            element
          );
      }
    );
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () =>
        initialize(document),
      {
        once: true
      }
    );
  } else {
    initialize(document);
  }

  document.addEventListener(
    'shopify:section:load',
    (event) =>
      initialize(
        event.target
      )
  );
})();